import { forwardRef, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from "@chakra-ui/react";
import { DownloadIcon } from "@chakra-ui/icons";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

const PW = 920;
const PH = 650;

const GRN_DARK  = "#1A5C38";
const GRN_MID   = "#276749";
const GRN_LIGHT = "#48BB78";
const GOLD      = "#C9A84C";
const BG        = "#F4F9F6";
const TXT_DARK  = "#1A202C";
const TXT_MID   = "#4A5568";

const formatDateBR = (d) => {
  if (!d) return "";
  try { return format(new Date(d), "dd 'de' MMMM 'de' yyyy", { locale: ptBR }); }
  catch { return String(d); }
};

const slugify = (s) =>
  String(s).normalize("NFD").replace(/[̀-ͯ]/g, "")
    .replace(/[^a-zA-Z0-9 ]/g, "").trim().replace(/\s+/g, "-").toLowerCase();

// Fetch any URL as base64 data-URL, bypassing browser cache.
// Returns null on failure (e.g. CORS not configured on S3).
const fetchAsDataURL = async (url) => {
  try {
    const res = await fetch(url, { cache: "no-cache", mode: "cors" });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = () => resolve(null);
      r.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

// ── Certificate visual ────────────────────────────────────────────────────────
export const CertificateView = forwardRef(function CertificateView(
  { userName, eventName, eventDate, theme, modality, eventImgSrc },
  innerRef,
) {
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const calc = () => {
      if (wrapRef.current) setScale(wrapRef.current.offsetWidth / PW);
    };
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, []);

  const label   = modality === "speaker" ? "Palestrante" : "Participante";
  const dateFmt = formatDateBR(eventDate);
  const today   = formatDateBR(new Date().toISOString());
  const logoUrl = process.env.PUBLIC_URL + "/logoBig.png";
  const nameUrl = process.env.PUBLIC_URL + "/name.png";

  const body = modality === "speaker"
    ? `palestrou no evento "${eventName}", realizado em ${dateFmt}, promovido pela ACAOL.`
    : `participou do evento "${eventName}", realizado em ${dateFmt}, promovido pela ACAOL.`;

  return (
    <Box ref={wrapRef} w="100%" position="relative" style={{ height: `${PH * scale}px` }}>
      <Box
        ref={innerRef}
        position="absolute" top={0} left={0}
        w={`${PW}px`} h={`${PH}px`}
        style={{ transform: `scale(${scale})`, transformOrigin: "top left" }}
        bg={BG} overflow="hidden"
        fontFamily="'Georgia', Georgia, serif"
      >
        {/* Border */}
        <Box position="absolute" inset={0} border={`8px solid ${GRN_DARK}`} zIndex={20} pointerEvents="none" />
        <Box position="absolute" inset="18px" border={`2px solid ${GOLD}`} zIndex={20} pointerEvents="none" />
        <Box position="absolute" top={0} left={0} right={0} h="8px" bg={GRN_MID} zIndex={21} />
        <Box position="absolute" bottom={0} left={0} right={0} h="8px" bg={GRN_MID} zIndex={21} />
        {[
          { top: "11px",    left:  "11px" },
          { top: "11px",    right: "11px" },
          { bottom: "11px", left:  "11px" },
          { bottom: "11px", right: "11px" },
        ].map((pos, i) => (
          <Box key={i} position="absolute" w="14px" h="14px" bg={GOLD}
            style={{ transform: "rotate(45deg)" }} zIndex={22} {...pos} />
        ))}

        {/* Watermark — eventImgSrc is a data-URL so html2canvas captures it cleanly */}
        {eventImgSrc && (
          <Box
            position="absolute" top="50%" left="50%"
            style={{ transform: "translate(-50%,-50%)" }}
            w="58%" zIndex={1} opacity={0.09} pointerEvents="none"
          >
            <Image src={eventImgSrc} w="100%" objectFit="contain" display="block" />
          </Box>
        )}

        {/* Header */}
        <Flex position="absolute" top="26px" left="34px" alignItems="center" gap="10px" zIndex={10}>
          <Image src={logoUrl} h="52px" objectFit="contain" />
          <Image src={nameUrl} h="21px" objectFit="contain" />
        </Flex>

        {/* Gold divider */}
        <Box
          position="absolute" top="94px" left="5%" right="5%" h="1.5px" zIndex={10}
          style={{ background: `linear-gradient(to right,transparent,${GOLD} 20%,${GOLD} 80%,transparent)` }}
        />

        {/* Content */}
        <Flex
          position="absolute" top="106px" left="40px" right="40px" bottom="28px"
          flexDir="column" alignItems="center" justifyContent="center"
          gap="13px" textAlign="center" zIndex={10}
        >
          <Box>
            <Text
              fontSize="54px" fontWeight={900} color={GRN_DARK}
              letterSpacing="10px" textTransform="uppercase" lineHeight={1}
            >
              Certificado
            </Text>
            <Text
              fontSize="14px" color={GOLD} letterSpacing="8px"
              textTransform="uppercase" mt="5px"
              fontFamily="Helvetica,Arial,sans-serif"
            >
              de {label}
            </Text>
          </Box>

          <Flex alignItems="center" gap="10px" w="52%">
            <Box flex={1} h="1px" bg={GRN_LIGHT} />
            <Box w="9px" h="9px" bg={GRN_LIGHT} borderRadius="1px"
              style={{ transform: "rotate(45deg)" }} />
            <Box flex={1} h="1px" bg={GRN_LIGHT} />
          </Flex>

          <Text fontSize="13px" color={TXT_MID} fontStyle="italic"
            fontFamily="Helvetica,Arial,sans-serif">
            Certificamos que
          </Text>

          <Box>
            <Text fontSize="42px" fontWeight={800} color={GRN_DARK} lineHeight={1.1}>
              {userName}
            </Text>
            <Box mx="auto" mt="8px" w="70%" h="2px"
              style={{ background: `linear-gradient(to right,transparent,${GRN_LIGHT},transparent)` }} />
          </Box>

          <Text
            fontSize="14.5px" color={TXT_DARK} lineHeight={1.85}
            maxW="760px" fontFamily="Helvetica,Arial,sans-serif"
          >
            {body}
          </Text>

          {theme && (
            <Text
              fontSize="12px" color={TXT_MID} fontStyle="italic"
              maxW="740px" lineHeight={1.7} fontFamily="Helvetica,Arial,sans-serif"
            >
              Tema: {theme}
            </Text>
          )}

          <Text fontSize="11px" color={TXT_MID} fontFamily="Helvetica,Arial,sans-serif" mt="2px">
            Emitido em {today}
          </Text>
        </Flex>
      </Box>
    </Box>
  );
});

// ── Modal ─────────────────────────────────────────────────────────────────────
const CertificateModal = ({ isOpen, onClose, cert }) => {
  const captureRef = useRef(null); // hidden full-size copy — no transform, no reflow
  const [busy, setBusy]           = useState(false);
  const [eventImgSrc, setEventImgSrc] = useState(null);
  const toast = useToast();

  useEffect(() => {
    setEventImgSrc(null);
    if (!cert?.eventImageUrl) return;
    fetchAsDataURL(cert.eventImageUrl).then((b64) => {
      if (b64) setEventImgSrc(b64);
      else setEventImgSrc(cert.eventImageUrl);
    });
  }, [cert?.eventImageUrl]);

  if (!cert) return null;

  const handleDownload = async () => {
    const el = captureRef.current;
    if (!el) return;
    setBusy(true);

    // Wait two frames so the hidden element has finished painting
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

    try {
      const canvas = await html2canvas(el, {
        scale:           2,
        useCORS:         true,
        allowTaint:      false,
        backgroundColor: BG,
        width:           PW,
        height:          PH,
        logging:         false,
      });

      const img = canvas.toDataURL("image/jpeg", 0.96);
      const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      doc.addImage(img, "JPEG", 0, 0, 841.89, 595.28);
      doc.save(
        `certificado-${cert.modality === "speaker" ? "palestrante" : "participacao"}-${slugify(cert.eventName)}.pdf`
      );
    } catch (err) {
      console.error("Erro ao gerar PDF:", err);
      toast({
        title: "Erro ao gerar PDF",
        description: err?.message || "Não foi possível gerar o certificado.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setBusy(false);
    }
  };

  const certProps = {
    userName:     cert.userName,
    eventName:    cert.eventName,
    eventDate:    cert.eventDate,
    theme:        cert.theme,
    modality:     cert.modality,
    eventImgSrc,
  };

  return (
    <>
      {/*
        Hidden capture target rendered at exactly PW wide so scale=1 inside
        CertificateView — no CSS transform, no layout reflow on capture.
      */}
      <Box
        position="fixed"
        top="-9999px"
        left="-9999px"
        w={`${PW}px`}
        zIndex={-1}
        aria-hidden="true"
        pointerEvents="none"
      >
        <CertificateView ref={captureRef} {...certProps} />
      </Box>

      <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered>
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent borderRadius="1.25rem" overflow="hidden" mx={4}>
          <ModalHeader borderBottom="1px solid" borderColor="gray.100"
            fontSize="1rem" fontWeight={700} color="green.700" pb={3}>
            Prévia do Certificado
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={6} px={[3, 6]}>
            <CertificateView {...certProps} />
            <Text fontSize="0.78rem" color="gray.400" textAlign="center" mt={3}>
              {eventImgSrc && eventImgSrc.startsWith("data:")
                ? "O PDF gerado será idêntico a esta prévia, incluindo a imagem de fundo."
                : "O PDF gerado será idêntico a esta prévia."}
            </Text>
          </ModalBody>
          <ModalFooter gap={3} borderTop="1px solid" borderColor="gray.100">
            <Button variant="ghost" size="sm" onClick={onClose}>Fechar</Button>
            <Button leftIcon={<DownloadIcon />} colorScheme="green" size="sm" px={6}
              isLoading={busy} loadingText="Gerando PDF..." onClick={handleDownload}>
              Baixar PDF
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default CertificateModal;
